/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { DateTime } from 'luxon'

import { getUserTimezone } from '@/insights/common/timezone'
import { formatDate12 } from '@/pages/assets/map/AssetTable/utils'
import type { Severity } from '@/pages/assets/map/types'
import { convertCelsius, tempUnitSuffix } from '@/utils/conversions'
import { formatDuration } from '@/utils/formatDate'

import type { ApiTimestamp, BlastTiming, InStoreAlert, InStoreSensor, Threshold } from './types'

export type Units = 'Imperial' | 'Metric'

export const formatTemp = (celsius: number | null | undefined, units: Units): string =>
  celsius == null ? '—' : `${convertCelsius(celsius, units).toFixed(1)}${tempUnitSuffix(units)}`

export const formatRange = (range: Threshold | null, units: Units): string =>
  range
    ? `${Math.round(convertCelsius(range.lowCelsius, units))} to ${Math.round(convertCelsius(range.highCelsius, units))}${tempUnitSuffix(units)}`
    : 'No range set'

export const formatPercent = (value: number | null | undefined): string =>
  value == null ? '—' : `${Math.round(value)}%`

export const formatTime = (value: ApiTimestamp | null | undefined): string =>
  value ? DateTime.fromJSDate(new Date(value)).toFormat('dd-MMM-yyyy hh:mm a') : '—'

/** formatTime in the user's profile timezone, with the zone's abbreviation (e.g. AEDT) on the end. */
export const formatTimeWithZone = (value: ApiTimestamp, zone: string = getUserTimezone()): string =>
  DateTime.fromJSDate(new Date(value), { zone }).toFormat('dd-MMM-yyyy hh:mm a ZZZZ')

/** Judged in Celsius, before any unit conversion; a location without a range never alarms. */
export const isOutOfRange = (sensor: InStoreSensor, range: Threshold | null): boolean =>
  sensor.type === 'temperature' &&
  sensor.temperatureCelsius != null &&
  range != null &&
  (sensor.temperatureCelsius < range.lowCelsius || sensor.temperatureCelsius > range.highCelsius)

export type SensorStatus = 'alarm' | 'online' | 'offline'

/**
 * Gray beats red beats green: an offline tag's reading is stale, so it is gray whatever
 * that last reading was; an online one is red outside the range and green inside it.
 */
export const sensorStatus = (sensor: InStoreSensor, range: Threshold | null): SensorStatus => {
  if (!sensor.online) return 'offline'
  return isOutOfRange(sensor, range) ? 'alarm' : 'online'
}

/** A temperature sensor whose tag is offline shows no current temperature, only its last known one. */
export const isOfflineTemperature = (sensor: InStoreSensor): boolean =>
  sensor.type === 'temperature' && sensor.online === false

/** "Last known temperature" and "Offline since", as the legacy Fresh screen showed on hover. */
export const offlineTooltip = (sensor: InStoreSensor, units: Units): string => {
  const lines = [`Last Known Temperature ${formatTemp(sensor.temperatureCelsius, units)}`]
  if (sensor.offlineSince) lines.push(`Offline since: ${formatDate12(sensor.offlineSince)}`)
  return lines.join('\n')
}

const SEVERITY_RANK: Record<Severity, number> = { critical: 3, warning: 2, informational: 1 }

/** The most severe of the alerts, for the list's alert symbol; null with no alerts. */
export const highestSeverity = (alerts: InStoreAlert[]): Severity | null =>
  alerts.reduce<Severity | null>((highest, alert) => {
    const severity = alert.severity.toLowerCase() as Severity
    if (!(severity in SEVERITY_RANK)) return highest
    return !highest || SEVERITY_RANK[severity] > SEVERITY_RANK[highest] ? severity : highest
  }, null)

/** The alert card takes ISO strings; the API's timestamps may already be parsed. */
export const toAlertRow = (alert: InStoreAlert) => ({
  name: alert.name,
  severity: alert.severity.toLowerCase(),
  raisedTime: new Date(alert.raisedTime).toISOString(),
})

export const doorLabel = (sensor: InStoreSensor): string => {
  const state = sensor.door?.state
  return state === 'open' ? 'Open' : state === 'closed' ? 'Closed' : '—'
}

/** The API's lookback for the last out-of-range reading (LOOKBACK_HOURS in api-insights). */
export const TIMING_LOOKBACK_HOURS = 48

export interface TimingLabel {
  text: string
  /** A projection from the recent trend rather than a measured time: shown negative and italic. */
  estimate: boolean
  title?: string
}

/**
 * A blast freezer sensor's time in range ("2h 15m"), or, while it is still out of range,
 * the estimated time until it gets there, negative ("-40m").
 */
export const formatTiming = (timing: BlastTiming | null | undefined, now: number): TimingLabel => {
  if (timing?.inRangeSince) {
    const since = new Date(timing.inRangeSince).getTime()
    return { text: formatDuration(Math.min(since, now), now), estimate: false, title: 'Time in range' }
  }
  if (timing?.inRangeSinceBeyondWindow) {
    return {
      text: `>${TIMING_LOOKBACK_HOURS}h`,
      estimate: false,
      title: `In range for more than ${TIMING_LOOKBACK_HOURS} hours`,
    }
  }
  if (timing?.estimatedInRangeAt) {
    const at = new Date(timing.estimatedInRangeAt).getTime()
    return {
      text: `-${formatDuration(now, Math.max(at, now))}`,
      estimate: true,
      title: 'Estimated time to reach the range, from the recent trend',
    }
  }
  return { text: '—', estimate: false }
}
